import React, { useState, useMemo } from 'react';
import Contestant from '../../../_types/Contestant';
import styled from '@emotion/styled';
import { useTheme } from '../../../../../context/PermissionsContext/ThemeContext/ThemeContext';
import { InputAdornment } from '@mui/material';
import { Search, PersonOff, Add } from '@mui/icons-material';
import { SearchInput } from '../../../../users/StyledInputs';

interface Props {
  onSelect: (contestant: Contestant) => void;
  contestants: Contestant[];
  selectedContestants: string[];
}

interface ThemeProps {
  theme?: any;
  isDark?: boolean;
}
const SearchContainer = styled.div`
  margin-bottom: 20px;
`;

const ListContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  margin-top: 20px;

  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const EmptyState = styled.div<ThemeProps>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
  background-color: ${({ theme, isDark }) =>
    theme?.background2 || (isDark ? '#1E1E1E' : '#f8f9fa')};
  border-radius: 12px;
  text-align: center;
  border: 1px solid
    ${({ theme, isDark }) => theme?.stroke || (isDark ? '#404040' : '#e0e0e0')};
  transition: background-color 0.2s ease, border-color 0.2s ease;
`;

const EmptyStateText = styled.div<ThemeProps>`
  font-size: 15px;
  font-weight: 500;
  color: ${({ theme, isDark }) =>
    theme?.textLight || (isDark ? '#B0B0B0' : '#666')};
  margin-top: 16px;
  font-family: Manrope;
`;

const ContestantCard = styled.div<ThemeProps>`
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 12px;
  background-color: ${({ theme, isDark }) =>
    theme?.background2 || (isDark ? '#1E1E1E' : 'rgba(240, 240, 243, 0.5)')};
  padding: 16px;
  transition: all 0.2s ease;
  border: 1px solid
    ${({ theme, isDark }) =>
      theme?.stroke || (isDark ? '#404040' : 'transparent')};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ isDark }) =>
      isDark
        ? '0 4px 12px rgba(0, 0, 0, 0.3)'
        : '0 4px 12px rgba(0, 0, 0, 0.05)'};
    border-color: ${({ theme, isDark }) =>
      theme?.primary || (isDark ? '#FF6A00' : '#e0e0e0')};
    background-color: ${({ theme, isDark }) =>
      theme?.background3 || (isDark ? '#2D2D2D' : 'rgba(245, 245, 248, 0.8)')};
  }

  &:active {
    transform: translateY(0);
  }
`;

const ContestantInfo = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

const Avatar = styled.img`
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 50%;
  border: 2px solid #fff;
`;

const ContestantName = styled.div<ThemeProps>`
  font-weight: 600;
  font-size: 15px;
  color: ${({ theme, isDark }) => theme?.text || (isDark ? '#FFFFFF' : '#333')};
  font-family: Manrope;
`;

const ContestantList: React.FC<Props> = ({
  contestants,
  onSelect,
  selectedContestants,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const { theme } = useTheme();
  const isDark = theme.isDark;

  const filteredContestants: Contestant[] = useMemo(() => {
    let availableContestants = contestants.filter(
      (contestant) =>
        !selectedContestants.some((selected) => selected === contestant.value)
    );

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      availableContestants = availableContestants.filter((contestant) =>
        contestant.label.toLowerCase().includes(searchLower)
      );
    }

    return availableContestants;
  }, [contestants, selectedContestants, searchTerm]);

  const renderSearchInput = () => (
    <SearchContainer>
      <SearchInput
        fullWidth
        placeholder="Search contestants..."
        value={searchTerm}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setSearchTerm(e.target.value)
        }
        sx={{
          width: '100%',
          marginBottom: '0px',
          '& .MuiInputBase-input': {
            color: (theme: any) => theme.custom.text,
          },
          '& .MuiInputBase-input::placeholder': {
            color: (theme: any) => theme.custom.textLight,
            opacity: 1,
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment
              position="start"
              sx={{ backgroundColor: 'transparent', width: '10px' }}
            >
              <Search sx={{ color: (theme: any) => theme.custom.textLight }} />
            </InputAdornment>
          ),
        }}
      />
    </SearchContainer>
  );

  const renderEmptyState = () => (
    <EmptyState theme={theme} isDark={isDark}>
      <PersonOff
        sx={{
          color: theme?.textLight || (isDark ? '#B0B0B0' : '#999'),
          fontSize: 40,
        }}
      />
      <EmptyStateText theme={theme} isDark={isDark}>
        {searchTerm
          ? 'No contestants found for your search'
          : 'No contestants available'}
      </EmptyStateText>
    </EmptyState>
  );

  const renderContestantList = () => (
    <ListContainer>
      {filteredContestants.map((contestant) => (
        <ContestantListItem
          key={contestant.value}
          onSelect={onSelect}
          contestant={contestant}
          theme={theme as any}
          isDark={isDark}
        />
      ))}
    </ListContainer>
  );

  return (
    <>
      {renderSearchInput()}
      {filteredContestants.length === 0
        ? renderEmptyState()
        : renderContestantList()}
    </>
  );
};

const ContestantListItem = ({
  contestant,
  onSelect,
  theme,
  isDark,
}: {
  contestant: Contestant;
  onSelect: (contestant: Contestant) => void;
  theme?: any;
  isDark?: boolean;
}) => {
  return (
    <ContestantCard
      onClick={() => onSelect(contestant)}
      theme={theme}
      isDark={isDark}
    >
      <ContestantInfo>
        <Avatar src={contestant.avatar} alt={contestant.label} />
        <ContestantName theme={theme} isDark={isDark}>
          {contestant.label}
        </ContestantName>
      </ContestantInfo>
      <Add sx={{ color: 'white', fontSize: 16 }} />
    </ContestantCard>
  );
};

export default ContestantList;
